/** A row's state marks and words (the tour itinerary workspace's; DESIGN's Icon-And-Words
 *  rule): a row's "changed", "added", "changed on the server" and "can't be saved" are an
 *  icon AND words — shown as icons with tooltips, and written into the row's accessible name
 *  (`stateWords`) — and Restore is one treatment everywhere (P2's: the restore icon,
 *  "Restore — …" on hover, "Restore <name>" as its name). */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { Box, IconButton, Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';

export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface RowState {
  changed: boolean;
  /** The first reason the row can't be saved, or null. */
  invalid: string | null;
  removed?: boolean;
  /** A row the draft adds: created on save (it reads "added", not "changed"). */
  added?: boolean;
  /** One of its fields changed on the server since the draft took it (Keep mine / Take
   *  theirs in its fields). */
  stale?: boolean;
  /** What a changed row was, in words ("was “Bangkok arrival”"), said after "changed". */
  was?: string | null;
}

/** " · changed · was … · can't save: …" — the state appended to a row's accessible name. */
export function stateWords({ changed, invalid, removed, added, stale, was }: RowState): string {
  return [
    removed ? 'removed — deleted on save' : null,
    added && !removed ? 'added — created on save' : null,
    changed && !removed && !added ? 'changed' : null,
    was && !removed ? was : null,
    stale && !removed ? 'changed on the server' : null,
    invalid ? `can’t save: ${invalid}` : null,
  ]
    .filter(Boolean)
    .map((w) => ` · ${w}`)
    .join('');
}

const MARK_SX = { fontSize: 16 } as const;

/** The row's state mark — ONE 16 px slot, kept in both modes: "can't save" wins over
 *  "changed on the server", which wins over "added", then "changed"; its tooltip (and the
 *  row's accessible name, `stateWords`) carries the rest. */
export function StateMarks({ changed, invalid, added, stale }: RowState) {
  const pending = added ? ' · added — created on save' : changed ? ' · changed — not saved yet' : '';
  return (
    <Box data-state-marks sx={{ display: 'flex', alignItems: 'center', width: 16, justifyContent: 'center', flex: 'none' }}>
      {invalid ? (
        <Tooltip title={`Can’t save: ${invalid}${pending}`}>
          <ErrorOutlineIcon data-mark="invalid" sx={{ ...MARK_SX, color: 'error.main' }} />
        </Tooltip>
      ) : stale ? (
        <Tooltip title={`Changed on the server — Keep mine or Take theirs in its fields${pending}`}>
          <SyncProblemIcon data-mark="stale" sx={{ ...MARK_SX, color: 'warning.main' }} />
        </Tooltip>
      ) : added ? (
        <Tooltip title="Added — created on save">
          <AddCircleOutlineIcon data-mark="added" sx={{ ...MARK_SX, color: 'warning.dark' }} />
        </Tooltip>
      ) : changed ? (
        <Tooltip title="Changed — not saved yet">
          <EditNoteOutlinedIcon data-mark="changed" sx={{ ...MARK_SX, color: 'warning.dark' }} />
        </Tooltip>
      ) : null}
    </Box>
  );
}

/** Put a removed saved row back (P2's treatment). */
export function RestoreButton({ name, hint, onRestore, tabIndex }: { name: string; hint: string; onRestore: () => void; tabIndex?: number }) {
  return (
    <Tooltip title={`Restore — ${hint}`}>
      <IconButton
        size="small"
        aria-label={`Restore ${name}`}
        data-restore
        {...(tabIndex !== undefined ? { tabIndex } : {})}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onRestore();
        }}
        sx={{ p: 0.25 }}
      >
        <RestoreIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
